import React, { useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import {
  canApprovePurchaseOrder,
  canCancelPurchaseOrder,
  canClosePurchaseOrder,
  canReceivePurchaseOrder,
  canReopenPurchaseOrder,
  canSendPurchaseOrder,
} from "../../components/forms/purchaseOrders/PurchaseOrderStatusBadge";
import { fetchLocationsForDropdown } from "../../shared/locationDropdowns";
import { readDefaultLocationFromStorage } from "../../shared/defaultLocationStorage";
import { buildPurchaseOrderPayload } from "../../purchaseOrders/purchaseOrderPayload";
import { handlePoApiResult } from "../../purchaseOrders/purchaseOrderApi";
import {
  attachLineItemTotals,
  createEmptyLineItem,
  createInitialPurchaseOrderFormState,
  mapDetailResponseToForm,
  summarizePurchaseOrderLines,
  unitOfMeasureFromAssemblyRaw,
  unitCostFromAssemblyRaw,
  validatePurchaseOrder,
} from "../../purchaseOrders/purchaseOrderFormUtils";
import type {
  AssemblyItemDropdownOption,
  AssemblyItemDropdownRaw,
  DropdownOption,
  PurchaseOrderDetail,
  PurchaseOrderDetailResponse,
  PurchaseOrderFormLineItem,
  PurchaseOrderFormState,
  PurchaseOrderLineSummary,
  PurchaseOrderReceivingInvoice,
  PurchaseOrderStatus,
  PurchaseOrderSummary,
} from "../../purchaseOrders/purchaseOrderTypes";
import { purchaseOrderIdFromResponse } from "../../purchaseOrders/purchaseOrderTypes";

type AddSnackbarFn = (snackbar: SnackbarData) => void;
type LogoutFn = () => void;

type SupplierDropdownOption = DropdownOption;
type LocationDropdownOption = DropdownOption;

interface EmptyModalState {
  open: boolean;
  childComponent: ReactNode;
}

export interface CreatePurchaseOrderViewModelState {
  purchaseOrder: PurchaseOrderFormState;
  setPurchaseOrderField: (field: keyof PurchaseOrderFormState, value: unknown) => void;
  onSupplierChange: (option: SupplierDropdownOption | null) => Promise<void>;
  onLocationChange: (option: DropdownOption | null) => void;
  lineItems: PurchaseOrderFormLineItem[];
  lineItemsWithTotals: PurchaseOrderFormLineItem[];
  lineSummary: PurchaseOrderLineSummary;
  summary: PurchaseOrderSummary | null;
  receivingInvoices: PurchaseOrderReceivingInvoice[];
  addLineItem: () => void;
  removeLineItem: (tempId: number | string) => Promise<void>;
  updateLineItem: (
    tempId: number | string,
    patch: Partial<PurchaseOrderFormLineItem>,
  ) => void;
  onLineItemAssemblySelect: (
    tempId: number | string,
    option: AssemblyItemDropdownOption | null,
  ) => void;
  supplierOptions: SupplierDropdownOption[];
  locationOptions: LocationDropdownOption[];
  assemblyItemOptions: AssemblyItemDropdownOption[];
  loading: boolean;
  saving: boolean;
  actionLoading: boolean;
  saveChanges: () => Promise<boolean>;
  createAndApprove: () => Promise<boolean>;
  approve: () => Promise<boolean>;
  send: () => Promise<boolean>;
  cancel: () => Promise<boolean>;
  closePo: () => Promise<boolean>;
  reopen: () => Promise<boolean>;
  init: (routePurchaseOrderId?: string) => Promise<void>;
  emptyModal: EmptyModalState;
  setEmptyModelOpen: Dispatch<SetStateAction<EmptyModalState>>;
  navigateDestination: string | null;
  showErrorModal: (message: string, title?: string) => void;
  purchaseOrderId: number | null;
  poStatus: PurchaseOrderStatus | string;
  poNumber: string | null;
  canApprove: boolean;
  canSend: boolean;
  canCancel: boolean;
  canClose: boolean;
  canReopen: boolean;
  canReceive: boolean;
}

export function useCreatePurchaseOrderViewModel(
  logout: LogoutFn,
  addSnackbar: AddSnackbarFn,
): CreatePurchaseOrderViewModelState {
  const [purchaseOrderId, setPurchaseOrderId] = useState<number | null>(null);
  const [poStatus, setPoStatus] = useState<PurchaseOrderStatus | string>("draft");
  const [poNumber, setPoNumber] = useState<string | null>(null);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderFormState>(() =>
    createInitialPurchaseOrderFormState(),
  );
  const [lineItems, setLineItems] = useState<PurchaseOrderFormLineItem[]>([]);
  const [summary, setSummary] = useState<PurchaseOrderSummary | null>(null);
  const [receivingInvoices, setReceivingInvoices] = useState<
    PurchaseOrderReceivingInvoice[]
  >([]);
  const [supplierOptions, setSupplierOptions] = useState<SupplierDropdownOption[]>(
    [],
  );
  const [locationOptions, setLocationOptions] = useState<LocationDropdownOption[]>(
    [],
  );
  const [assemblyItemOptions, setAssemblyItemOptions] = useState<
    AssemblyItemDropdownOption[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [navigateDestination, setNavigateDestination] = useState<string | null>(
    null,
  );
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });

  const hasReceivedAmount = Number(summary?.received_amount ?? 0) > 0;

  const lineSummary = useMemo(
    () => summarizePurchaseOrderLines(lineItems),
    [lineItems],
  );

  const lineItemsWithTotals = useMemo(
    () => attachLineItemTotals(lineItems),
    [lineItems],
  );

  const showErrorModal = (message: string, title = "Error") => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={message}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getSuppliers = async function () {
    const callResult = await makeGetApiCall("manufacturersForDropdown");
    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not fetch suppliers.",
      onSuccess: (data) => {
        const record = data as { suppliers?: SupplierDropdownOption[] };
        const list =
          record.suppliers || (data as SupplierDropdownOption[]) || [];
        setSupplierOptions(
          list.map((s) => ({
            value:
              (s as { value?: number; id?: number }).value ??
              (s as { id?: number }).id!,
            label:
              (s as { label?: string; name?: string }).label ??
              (s as { name?: string }).name ??
              "",
          })),
        );
      },
    });
  };

  const getLocations = async function () {
    const callResult = await fetchLocationsForDropdown();
    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not fetch locations.",
      onSuccess: (data) => {
        setLocationOptions((data as LocationDropdownOption[]) || []);
      },
    });
  };

  const getAllAssemblyItems = async function (
    supplierId: number | string | null = null,
  ) {
    const payload = supplierId ? { supplier_id: supplierId } : {};
    const callResult = await makePostApiCall("allAssemblyItems", payload);
    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not load items.",
      onSuccess: (data) => {
        const items =
          (data as { assembly_items?: AssemblyItemDropdownRaw[] })
            .assembly_items ?? [];
        setAssemblyItemOptions(
          items.map((item) => ({
            value: item.id,
            label: item.product_name ?? "",
            raw: item,
          })),
        );
      },
    });
  };

  const applyLoadedPurchaseOrder = (
    data: PurchaseOrderDetailResponse,
  ): Partial<PurchaseOrderDetail> => {
    const loaded = mapDetailResponseToForm(data);
    setPurchaseOrderId(loaded.purchaseOrderId);
    setPoStatus(loaded.poStatus);
    setPoNumber(loaded.poNumber);
    setSummary(loaded.summary);
    setReceivingInvoices(loaded.receivingInvoices);
    setPurchaseOrder(loaded.purchaseOrder);
    setLineItems(loaded.lineItems);
    return data.purchase_order ?? {};
  };

  const loadPurchaseOrder = async function (id: number | string) {
    const callResult = await makePostApiCall(`purchaseOrders/${id}`, {});

    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not load purchase order.",
      onSuccess: async (data) => {
        const po = applyLoadedPurchaseOrder(data as PurchaseOrderDetailResponse);
        return getAllAssemblyItems(po.supplier_id ?? po.supplier?.id ?? null);
      },
    });
  };

  const init = async function (routePurchaseOrderId?: string) {
    setLoading(true);
    setNavigateDestination(null);

    const suppliersOk = await getSuppliers();
    if (!suppliersOk) {
      setLoading(false);
      return;
    }

    await getLocations();

    if (routePurchaseOrderId) {
      await loadPurchaseOrder(routePurchaseOrderId);
      setLoading(false);
      return;
    }

    setPurchaseOrderId(null);
    setPoStatus("draft");
    setPoNumber(null);
    setSummary(null);
    setReceivingInvoices([]);
    setPurchaseOrder(
      createInitialPurchaseOrderFormState(readDefaultLocationFromStorage()),
    );
    setLineItems([]);
    await getAllAssemblyItems();
    setLoading(false);
  };

  const runAction = async (endpoint: string, successMessage: string) => {
    if (!purchaseOrderId) return false;

    setActionLoading(true);
    const callResult = await makePostApiCall(
      `purchaseOrders/${purchaseOrderId}/${endpoint}`,
      {},
    );
    setActionLoading(false);

    return handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Action failed.",
      onSuccess: async (data) => {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            (data as { message?: string })?.message || successMessage,
            SnackbarType.Success,
          ),
        );
        await loadPurchaseOrder(purchaseOrderId);
      },
    });
  };

  const setPurchaseOrderField = (
    field: keyof PurchaseOrderFormState,
    value: unknown,
  ) => {
    setPurchaseOrder((prev: PurchaseOrderFormState) => ({ ...prev, [field]: value }));
  };

  const onLocationChange = (option: DropdownOption | null) => {
    setPurchaseOrder((prev: PurchaseOrderFormState) => ({
      ...prev,
      location: option ?? null,
    }));
  };

  const onSupplierChange = async (option: SupplierDropdownOption | null) => {
    const supplierId = option?.value ?? null;
    setPurchaseOrder((prev: PurchaseOrderFormState) => ({
      ...prev,
      supplier_id: supplierId,
      supplier_label: option?.label ?? "",
    }));
    await getAllAssemblyItems(supplierId);
  };

  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      createEmptyLineItem(generateUniqueNumber()),
    ]);
  };

  const removeLineItem = async (tempId: number | string) => {
    const row = lineItems.find((r) => r.temp_id === tempId);
    if (!row) return;

    if (row.line_locked) {
      showErrorModal("Lines with received quantity cannot be removed.");
      return;
    }

    if (!row.id) {
      setLineItems((prev) => prev.filter((r) => r.temp_id !== tempId));
      return;
    }

    const callResult = await makePostApiCall(
      `deletePurchaseOrderItem/${row.id}`,
      {},
    );

    await handlePoApiResult(callResult, {
      logout,
      onError: showErrorModal,
      errorMessage: "Could not delete purchase order item.",
      onSuccess: async (data) => {
        setLineItems((prev) => prev.filter((r) => r.temp_id !== tempId));
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            (data as { message?: string })?.message ||
              "Purchase order item deleted.",
            SnackbarType.Success,
          ),
        );
        if (purchaseOrderId != null) {
          await loadPurchaseOrder(purchaseOrderId);
        }
      },
    });
  };

  const updateLineItem = (
    tempId: number | string,
    patch: Partial<PurchaseOrderFormLineItem>,
  ) => {
    setLineItems((prev) =>
      prev.map((row) => (row.temp_id === tempId ? { ...row, ...patch } : row)),
    );
  };

  const onLineItemAssemblySelect = (
    tempId: number | string,
    option: AssemblyItemDropdownOption | null,
  ) => {
    const raw = option?.raw;
    updateLineItem(tempId, {
      assembly_item_id: option?.value ?? null,
      item_name: raw?.name || raw?.product_name || option?.label || "",
      supplier_plu: raw?.plu ?? raw?.supplier_plu ?? "",
      description: raw?.description || raw?.name || raw?.product_name || "",
      unit_of_measure: unitOfMeasureFromAssemblyRaw(raw),
      unit_cost: unitCostFromAssemblyRaw(raw),
    });
  };

  const persistPurchaseOrder = async function ({
    approveAfterSave = false,
  }: {
    approveAfterSave?: boolean;
  }) {
    const validation = validatePurchaseOrder(purchaseOrder, lineItems);
    if (!validation.valid) {
      showErrorModal(validation.message);
      return false;
    }

    setSaving(true);
    try {
      const payload = buildPurchaseOrderPayload(
        purchaseOrder,
        lineItems,
        supplierOptions,
      );

      const wasCreate = purchaseOrderId == null;
      let id: number | null = purchaseOrderId;
      let callResult;

      if (id != null) {
        callResult = await makePutApiCall(`purchaseOrders/${id}`, payload);
      } else {
        callResult = await makePostApiCall("createPurchaseOrder", payload);
      }

      if (
        !(await handlePoApiResult(callResult, {
          logout,
          onError: showErrorModal,
          errorMessage: "Could not save purchase order.",
        }))
      ) {
        return false;
      }

      if (id == null) {
        id = purchaseOrderIdFromResponse(
          callResult.data as Parameters<typeof purchaseOrderIdFromResponse>[0],
        );
      }

      if (approveAfterSave && id != null) {
        const approveResult = await makePostApiCall(
          `purchaseOrders/${id}/approve`,
          {},
        );

        if (
          !(await handlePoApiResult(approveResult, {
            logout,
            onError: (message) => {
              showErrorModal(
                message ||
                  "Purchase order was saved but could not be approved.",
              );
            },
            errorMessage:
              "Purchase order was saved but could not be approved.",
            onSuccess: async (data) => {
              addSnackbar(
                new SnackbarData(
                  generateUniqueNumber(),
                  (data as { message?: string })?.message ||
                    "Purchase order created and approved.",
                  SnackbarType.Success,
                ),
              );

              if (wasCreate && id != null) {
                setNavigateDestination(`/purchase-orders/${id}`);
              } else if (id != null) {
                await loadPurchaseOrder(id);
              }
            },
          }))
        ) {
          if (wasCreate && id != null) {
            setNavigateDestination(`/purchase-orders/${id}`);
          } else if (id != null) {
            await loadPurchaseOrder(id);
          }
          return false;
        }

        return true;
      }

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          (callResult.data as { message?: string })?.message ||
            (wasCreate
              ? "Purchase order draft saved."
              : "Purchase order saved."),
          SnackbarType.Success,
        ),
      );

      if (wasCreate && id != null) {
        setNavigateDestination(`/purchase-orders/${id}`);
      } else if (id != null) {
        await loadPurchaseOrder(id);
      }

      return true;
    } finally {
      setSaving(false);
    }
  };

  const saveChanges = () => persistPurchaseOrder({ approveAfterSave: false });
  const createAndApprove = () =>
    persistPurchaseOrder({ approveAfterSave: true });
  const approve = () => runAction("approve", "Purchase order approved.");
  const send = () => runAction("send", "Purchase order sent to supplier.");
  const cancel = () => runAction("cancel", "Purchase order cancelled.");
  const closePo = () => runAction("close", "Purchase order closed.");
  const reopen = () => runAction("reopen", "Purchase order reopened.");

  return {
    purchaseOrder,
    setPurchaseOrderField,
    onSupplierChange,
    onLocationChange,
    lineItems,
    lineItemsWithTotals,
    lineSummary,
    summary,
    receivingInvoices,
    addLineItem,
    removeLineItem,
    updateLineItem,
    onLineItemAssemblySelect,
    supplierOptions,
    locationOptions,
    assemblyItemOptions,
    loading,
    saving,
    actionLoading,
    saveChanges,
    createAndApprove,
    approve,
    send,
    cancel,
    closePo,
    reopen,
    init,
    emptyModal,
    setEmptyModelOpen,
    navigateDestination,
    showErrorModal,
    purchaseOrderId,
    poStatus,
    poNumber,
    canApprove: canApprovePurchaseOrder(poStatus),
    canSend: canSendPurchaseOrder(poStatus),
    canCancel: canCancelPurchaseOrder(poStatus) && !hasReceivedAmount,
    canClose: canClosePurchaseOrder(poStatus),
    canReopen: canReopenPurchaseOrder(poStatus),
    canReceive: canReceivePurchaseOrder(poStatus),
  };
}

/** @deprecated Use `useCreatePurchaseOrderViewModel` */
export const CreatePurchaseOrderViewModel = useCreatePurchaseOrderViewModel;
